// Marketing-site content (operator CMS). Read via the public siteContentGet
// endpoint; save via the platformAdmin-guarded siteContentSave callable. Media
// (logo, tutorial videos, thumbnails) uploads to Cloud Storage under /site/**.
import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { functions, storage, siteContentUrl } from './firebase';

export interface Bi { ar: string; en: string }
export interface SiteVideo { id: string; title: Bi; url: string; thumb: string }
export interface SiteDoc { id: string; title: Bi; body: Bi }
export interface SiteContent {
  logoUrl: string;
  brandName: Bi;
  brandSub: Bi;
  announcement: Bi;
  hero: { title: Bi; sub: Bi; ctaTrial: Bi; ctaDemo: Bi };
  proMonthly: string;
  proYearly: string;
  videos: SiteVideo[];
  docs: SiteDoc[];
}

const bi = (ar = '', en = ''): Bi => ({ ar, en });

export const DEFAULT_SITE: SiteContent = {
  logoUrl: '',
  brandName: bi('واتاق', 'WaTag'),
  brandSub: bi('WaTag · بواسطة تاقنولوجيز', 'WaTag · by Tagnologys'),
  announcement: bi(),
  hero: { title: bi(), sub: bi(), ctaTrial: bi(), ctaDemo: bi() },
  proMonthly: '14',
  proYearly: '96',
  videos: [],
  docs: [],
};

function merge(d: Partial<SiteContent> | undefined): SiteContent {
  const x = d ?? {};
  return {
    ...DEFAULT_SITE,
    ...x,
    brandName: { ...DEFAULT_SITE.brandName, ...(x.brandName ?? {}) },
    brandSub: { ...DEFAULT_SITE.brandSub, ...(x.brandSub ?? {}) },
    announcement: { ...DEFAULT_SITE.announcement, ...(x.announcement ?? {}) },
    hero: {
      title: { ...bi(), ...(x.hero?.title ?? {}) },
      sub: { ...bi(), ...(x.hero?.sub ?? {}) },
      ctaTrial: { ...bi(), ...(x.hero?.ctaTrial ?? {}) },
      ctaDemo: { ...bi(), ...(x.hero?.ctaDemo ?? {}) },
    },
    videos: Array.isArray(x.videos) ? x.videos : [],
    docs: Array.isArray(x.docs) ? x.docs : [],
  };
}

export function useSiteContent(): { content: SiteContent; loading: boolean; error: string | null; reload: () => void } {
  const [content, setContent] = useState<SiteContent>(DEFAULT_SITE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  function reload() {
    setLoading(true);
    setError(null);
    fetch(siteContentUrl)
      .then((r) => r.json())
      .then((d) => setContent(merge(d)))
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }
  useEffect(() => { reload(); }, []);
  return { content, loading, error, reload };
}

const saveFn = httpsCallable<SiteContent, { ok: boolean }>(functions, 'siteContentSave');
export const saveSiteContent = (c: SiteContent) => saveFn(c).then((r) => r.data);

// Upload a file to Cloud Storage under /site/<folder>/ and return its public URL.
export async function uploadSiteMedia(file: File, folder: 'logo' | 'video' | 'thumb'): Promise<string> {
  const clean = file.name.replace(/[^a-zA-Z0-9._-]/g, '_').slice(-60);
  const r = ref(storage, `site/${folder}/${Date.now()}_${clean}`);
  await uploadBytes(r, file, file.type ? { contentType: file.type } : undefined);
  return getDownloadURL(r);
}

export const newId = () => Math.random().toString(36).slice(2, 10);
